import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import CostCentersService from 'services/ServiceCOSTCENTERS';
import * as yup from 'yup';

/**
 * @typedef {Object} CostCenter
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */

/**
 * Hook para buscar centros de custo.
 * @property {Object} props
 * @property {string} [login] - O login para buscar os centros de custo com base em permissão
 * @returns
 */
const useCostCenters = ({ login } = { login: null }) => {
  const [state, setState] = useState({
    /** @type {CostCenter[]} */
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0
  });

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      let apiResponse;
      if (login) {
        apiResponse = await CostCentersService.findAllowedByLogin(login);
      } else {
        apiResponse = await CostCentersService.findAll(false);
      }

      // Valida a resposta completa da API usando costCenterResponseSchema
      const validatedResponse = await costCenterResponseSchema.validate(
        apiResponse.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        const costCenterList = validatedResponse.data ?? [];
        const quantity = validatedResponse.totalRegisters ?? 0;

        setState((prevState) => ({
          ...prevState,
          response: costCenterList,
          totalRegisters: quantity,
          isLoading: false,
          isError: false,
          error: null
        }));
      } else {
        throw new Error(
          validatedResponse.message ||
            'Dados de centros de custo não encontrados ou inválidos.'
        );
      }
    } catch (err) {
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.message || 'Erro ao carregar centros de custo';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        response: [],
        totalRegisters: 0,
        isLoading: false,
        isError: true,
        error: err
      }));
    }
  }, [login]);

  /**
   * Busca um centro de custo pelo ID e valida a resposta com o esquema Yup.
   * @param {string} id - O ID do centro de custo a ser buscado.
   * @returns {Promise<CostCenter|null>} O centro de custo encontrado ou null em caso de erro.
   */
  const fetchCostCenterById = useCallback(async (id) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));
    try {
      const response = await CostCentersService.findById(id);
      const validatedResponse = await singleCostCenterResponseSchema.validate(
        response.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: false,
          error: null
        }));
        return validatedResponse.data;
      } else {
        throw new Error(
          validatedResponse.message ||
            'Dados do centro de custo não encontrados.'
        );
      }
    } catch (err) {
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.message ||
            'Erro ao carregar centro de custo por ID';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
      return null;
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    ...state,
    fetch,
    fetchCostCenterById
  };
};

export default useCostCenters;

const costCenterSchema = yup.object().shape({
  id: yup.number().integer().required('ID é obrigatório'),
  name: yup.string().required('Nome é obrigatório'),
  description: yup.string().required('Descrição é obrigatória')
});

const costCenterResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: yup.array().of(costCenterSchema).required('Dados são obrigatórios'),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios'),
  totalRegisters: yup.number().integer().nullable()
});

const singleCostCenterResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: costCenterSchema.nullable(),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios')
});
